<script setup lang="ts">
/**
 * Forum-Post-Zeile nach aktuellem Client (aus echten Screenshots abgenommen):
 * volle Breite, Tag-Pill oben, fetter Titel, Autor-Zeile, Meta-Zeile
 * (Envelope-Chip + Kommentar-Zähler + Aktivität) — rechts das Post-Thumbnail.
 */
withDefaults(defineProps<{
  title: string
  author: string
  avatarColor?: string
  tags?: { label: string, emoji?: string }[]
  messages?: number
  activity?: string
  highlighted?: boolean
  /** Post-Thumbnail rechts (Clarify-Logo, wie das Bot-Embed-Thumbnail). */
  thumbnail?: boolean
}>(), {
  avatarColor: '#e8a33d',
  tags: () => [],
  messages: 0,
  activity: '9d ago',
  highlighted: false,
  thumbnail: true
})
</script>

<template>
  <div class="dc-post" :class="{ 'is-highlighted': highlighted }">
    <div class="dc-post-main">
      <div v-if="tags.length" class="dc-post-tags">
        <DiscordTag v-for="t in tags" :key="t.label" :label="t.label" :emoji="t.emoji" />
      </div>
      <div class="dc-post-title">{{ title }}</div>
      <div class="dc-post-author">{{ author }}</div>
      <div class="dc-post-meta">
        <span class="dc-post-envelope" aria-hidden="true">
          <UIcon name="i-lucide-mail" class="size-[14px]" />
        </span>
        <span class="dc-post-count">
          <UIcon name="i-lucide-message-circle" class="size-[14px]" aria-hidden="true" />
          {{ messages }}
        </span>
        <span class="dc-post-dot" aria-hidden="true" />
        <span>{{ activity }}</span>
      </div>
    </div>
    <img v-if="thumbnail" class="dc-post-thumb" src="/brand/clarify-icon-square.svg" alt="" width="64" height="64">
  </div>
</template>

<style scoped>
.dc-post {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: var(--dc-bg-card);
  border: 1px solid var(--dc-border-card);
  border-radius: var(--dc-radius-card);
  padding: 14px 16px;
  font-family: var(--font-discord);
  transition: border-color var(--dur-fast) var(--ease-out);
}

.dc-post:hover {
  border-color: var(--dc-ink-channel);
}

.dc-post.is-highlighted {
  border-color: var(--nb-accent-soft);
  box-shadow: 0 0 24px -6px var(--nb-accent-glow);
}

.dc-post-main {
  flex: 1;
  min-width: 0;
}

.dc-post-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.dc-post-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--dc-ink-header);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dc-post-author {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--dc-ink);
  margin-bottom: 9px;
}

.dc-post-meta {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 0.8rem;
  color: var(--dc-ink-muted);
}

.dc-post-envelope {
  display: grid;
  place-items: center;
  width: 26px;
  height: 22px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: 6px;
  color: var(--dc-ink-muted);
}

.dc-post-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.dc-post-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--dc-ink-muted);
}

.dc-post-thumb {
  flex-shrink: 0;
  border-radius: 12px;
  display: block;
}
</style>
